import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { COUNTRY_CURRENCY_MAP, DEFAULT_CURRENCY, getAvailableCurrencies, getCurrencyConfig } from "@/config/pricing";
import { CurrencyCode, CurrencyContextType } from "@/types/billing/currency";

const STORAGE_KEY = "helpgenie_preferred_currency";

/**
 * Detect currency from browser locale
 * Uses navigator.language to extract country code and map to currency
 */
const detectCurrencyFromLocale = (): CurrencyCode => {
  try {
    const locale = navigator.language || navigator.languages?.[0];

    if (locale) {
      // Extract country code from locale (e.g., "en-NZ" -> "NZ", "de-DE" -> "DE")
      const parts = locale.split("-");
      const countryCode = parts[parts.length - 1]?.toUpperCase();

      if (countryCode && COUNTRY_CURRENCY_MAP[countryCode]) {
        return COUNTRY_CURRENCY_MAP[countryCode];
      }
    }
  } catch (e) {
    console.warn("Currency detection failed:", e);
  }

  return DEFAULT_CURRENCY;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
  userBillingCurrency?: string | null;
}

export const CurrencyProvider = ({ children, userBillingCurrency }: CurrencyProviderProps) => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [isDetected, setIsDetected] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [billingCurrency, setBillingCurrencyState] = useState<CurrencyCode | null>(null);

  // Initialize currency on mount
  useEffect(() => {
    let stored: CurrencyCode | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY) as CurrencyCode | null;
    } catch {
      // localStorage is inaccessible in some embedded/cross-origin contexts (e.g. iframe embeds)
    }
    const availableCurrencies = getAvailableCurrencies();

    if (stored && availableCurrencies.includes(stored)) {
      // User previously selected a currency
      setCurrencyState(stored);
      setIsDetected(false);
    } else {
      // Auto-detect from browser locale
      const detected = detectCurrencyFromLocale();
      setCurrencyState(detected);
      setIsDetected(true);
    }

    setIsInitialized(true);
  }, []);

  // Sync billing currency from user profile
  useEffect(() => {
    if (userBillingCurrency) {
      setBillingCurrencyState(userBillingCurrency as CurrencyCode);
      setCurrencyState(userBillingCurrency as CurrencyCode);
    } else {
      setBillingCurrencyState(null);
    }
  }, [userBillingCurrency]);

  const setCurrency = useCallback(
    (code: CurrencyCode) => {
      // If billing currency is locked, don't allow changing
      if (billingCurrency) {
        console.warn("Currency is locked to billing currency:", billingCurrency);
        return;
      }
      setCurrencyState(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {
        // localStorage is inaccessible in some embedded/cross-origin contexts (e.g. iframe embeds)
      }
      setIsDetected(false);
    },
    [billingCurrency],
  );

  const setBillingCurrency = useCallback((code: CurrencyCode | null) => {
    setBillingCurrencyState(code);
    if (code) {
      setCurrencyState(code);
    }
  }, []);

  // Effective currency: billing currency takes precedence if set
  const effectiveCurrency = billingCurrency || currency;

  const contextValue = useMemo<CurrencyContextType>(
    () => ({
      currency: effectiveCurrency,
      currencyConfig: getCurrencyConfig(effectiveCurrency),
      setCurrency,
      isDetected: !billingCurrency && isDetected,
      isLocked: !!billingCurrency,
      setBillingCurrency,
    }),
    [effectiveCurrency, setCurrency, isDetected, billingCurrency, setBillingCurrency],
  );

  // Prevent flash of default currency before initialization
  if (!isInitialized) {
    return null;
  }

  return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};
